import { createApi, fetchBaseQuery, FetchBaseQueryError } from '@reduxjs/toolkit/query/react'
import { AppState } from '@redux/store'
import { ActionModule } from '@typesApp/ActionModule'
import { HostSession, HostEnvironment } from '@typesApp/Environment'
import {
  Session,
  CustomerContext,
  CommerceCustomer,
  CommerceCustomerContext,
  Customer,
  WCTokens,
} from '@typesApp/Session'
import { apiConfigurationError, getCommerceApiUrl, getRequestHeaders } from '@utils/apiUtil'
import { createGuestSession, createSessionWithToken, toCustomer, toCustomerContext } from '@utils/sessionApiUtil'

const INVALID_SESSION: Session = {
  customer: {
    id: '',
  },
  userId: '',
  cartId: '',
  sessionId: '',
  isGuest: false,
  isPartiallyAuthenticated: false,
  isAnonymous: false,
}

export const sessionsApi = createApi({
  reducerPath: 'sessions',
  baseQuery: fetchBaseQuery(),
  keepUnusedDataFor: 300,
  tagTypes: ['Session', 'CustomerContext', 'AuthToken'],

  endpoints: builder => ({
    getSession: builder.query<Session, string>({
      providesTags: [{ type: 'Session' }],
      queryFn: async (sessionId, queryApi, _extraOptions, fetchFn) => {
        const state = queryApi.getState() as AppState
        const hostSession = state.connector.session as HostSession
        const isPartiallyAuthenticated = hostSession?.isPartiallyAuthenticated
        const env = state.connector?.environment as HostEnvironment
        const actionModule = state.connector.actionModule as ActionModule

        const isInvalid = state.connector.isLoggingOut || state.connector.sessionExpired
        if (isInvalid) {
          return { data: INVALID_SESSION }
        }

        if (hostSession?.isGuest) {
          let customer: Customer | undefined

          // always fetch latest customer id from commerce
          if (typeof actionModule?.getCustomer === 'function') {
            try {
              customer = await actionModule.getCustomer()
            } catch (error) {
              console.error('Error calling get customer API from action module', error)
              return { error: error as FetchBaseQueryError }
            }
          }

          const session = createGuestSession(sessionId, hostSession, customer)
          return { data: session }
        }

        // always fetch latest customer id from commerce
        if (typeof actionModule?.getCustomer === 'function') {
          try {
            const customer = await actionModule.getCustomer()
            const session = createSessionWithToken(sessionId, hostSession, customer)
            return { data: session }
          } catch (error) {
            console.error('Error calling get customer API from action module', error)
            return { error: error as FetchBaseQueryError }
          }
        }

        if (!env?.ecommerceTransactionHost) {
          return apiConfigurationError('customer')
        }

        try {
          const headers = getRequestHeaders(isPartiallyAuthenticated, hostSession)
          const url = getCommerceApiUrl(env, 'customer')
          const result = (await fetchFn({
            url,
            headers,
            method: 'GET',
            credentials: 'include',
          })) as { data: CommerceCustomer; error?: unknown }

          if (result.error) {
            return { error: result.error as FetchBaseQueryError }
          }

          const customer = toCustomer(result.data)
          const session = createSessionWithToken(sessionId, hostSession, customer)
          return { data: session }
        } catch (error) {
          console.error('Error calling getSession API', error)
          return { error: error as FetchBaseQueryError }
        }
      },
    }),

    getCustomerContext: builder.query<CustomerContext, void>({
      providesTags: [{ type: 'CustomerContext' }],
      queryFn: async (args, queryApi, _extraOptions, fetchFn) => {
        const state = queryApi.getState() as AppState
        const hostSession = state.connector.session as HostSession
        const isPartiallyAuthenticated = hostSession?.isPartiallyAuthenticated
        const env = state.connector?.environment as HostEnvironment
        const actionModule = state.connector.actionModule as ActionModule

        const isInvalid = state.connector.isLoggingOut || state.connector.sessionExpired
        if (isInvalid) {
          return { data: { sessionId: '', isPartiallyAuthenticated: false } }
        }

        if (typeof actionModule?.getCustomerContext === 'function') {
          try {
            const context = await actionModule.getCustomerContext()
            return { data: { ...context, sessionId: `${context?.sessionId ?? ''}` } }
          } catch (error) {
            console.error('Error calling customer context API from action module', error)
            return { error: error as FetchBaseQueryError }
          }
        }

        if (!env?.ecommerceTransactionHost) {
          return apiConfigurationError('customerContext')
        }

        try {
          const headers = getRequestHeaders(isPartiallyAuthenticated, hostSession)
          const url = getCommerceApiUrl(env, 'customerContext')
          const result = (await fetchFn({
            url,
            headers,
            method: 'GET',
            credentials: 'include',
          })) as { data: CommerceCustomerContext; error?: unknown }

          if (result.error) {
            return { error: result.error as FetchBaseQueryError }
          }

          const context = toCustomerContext(result.data)
          return { data: context }
        } catch (error) {
          console.error('Error calling customer context API', error)
          return { error: error as FetchBaseQueryError }
        }
      },
    }),

    getCustomerContextByTokens: builder.query<CustomerContext, WCTokens>({
      providesTags: [{ type: 'CustomerContext' }],
      queryFn: async (wcTokens, queryApi, _extraOptions, fetchFn) => {
        const state = queryApi.getState() as AppState
        const env = state.connector?.environment as HostEnvironment

        const isInvalid = state.connector.isLoggingOut || state.connector.sessionExpired
        if (isInvalid) {
          return { data: { sessionId: '', isPartiallyAuthenticated: false } }
        }

        if (!env?.ecommerceTransactionHost) {
          return apiConfigurationError('customerContext')
        }

        try {
          const headers = getRequestHeaders(Boolean(wcTokens.isPartiallyAuthenticated), wcTokens)
          const url = getCommerceApiUrl(env, 'customerContext')
          const result = (await fetchFn({
            url,
            headers,
            method: 'GET',
            credentials: 'include',
          })) as { data: CommerceCustomerContext; error?: unknown }

          if (result.error) {
            return { error: result.error as FetchBaseQueryError }
          }

          const context = toCustomerContext(result.data)
          return { data: context }
        } catch (error) {
          console.error('Error calling customer context API', error)
          return { error: error as FetchBaseQueryError }
        }
      },
    }),
  }),
})

export const {
  useGetSessionQuery,
  useLazyGetSessionQuery,
  useLazyGetCustomerContextQuery,
  useLazyGetCustomerContextByTokensQuery,
  usePrefetch: useSessionPrefetch,
} = sessionsApi
